import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

type SetTokenAmountFn = (amount: string | undefined) => void

type AddLiquidityFlowContextValue = {
  poolId: string | undefined
  initialSelectedPoolId?: string
  setPoolId: (poolId: string | undefined) => void
  token1Amount: string | undefined
  setToken1Amount: SetTokenAmountFn
  token2Amount: string | undefined
  setToken2Amount: SetTokenAmountFn
}

const AddLiquidityFlowContext = createContext<
  AddLiquidityFlowContextValue | undefined
>(undefined)

export const useAddLiquidityFlowContext = () => {
  const context = useContext(AddLiquidityFlowContext)
  if (!context) {
    throw new Error(
      "AddLiquidityFlowContext used outside of AddLiquidityFlowProvider component",
    )
  }

  return context
}

export default function AddLiquidityFlowProvider({
  children,
  initialPoolId,
}: {
  children: ReactNode
  initialPoolId?: string
}) {
  const [poolId, setPoolId] = useState<string | undefined>()

  const [initialSelectedPoolId, setInitialPooLId] = useState<
    string | undefined
  >(initialPoolId)

  useEffect(() => {
    setInitialPooLId(initialPoolId)
  }, [initialPoolId])

  const [token1Amount, setToken1Amount] = useState<string | undefined>()
  const [token2Amount, setToken2Amount] = useState<string | undefined>()

  const value = useMemo(
    () => ({
      poolId,
      setPoolId: (newPoolId: string | undefined) => {
        setInitialPooLId(undefined)
        setPoolId(newPoolId)
      },
      initialSelectedPoolId,
      token1Amount,
      setToken1Amount,
      token2Amount,
      setToken2Amount,
    }),
    [poolId, token1Amount, token2Amount, initialSelectedPoolId],
  )

  return (
    <AddLiquidityFlowContext.Provider value={value}>
      {children}
    </AddLiquidityFlowContext.Provider>
  )
}
